/* eslint-disable react-hooks/exhaustive-deps */

import { memo, useEffect, useState } from "react";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import DatatypeSelector from "./DatatypeSelector";
import QuantityEntry, { displayStringFromFHIRQuantity } from "./QuantityEntry";
import RangeEntry, { displayStringFromFHIRRange } from "./RangeEntry";
import TimingEntry, { displayStringFromFHIRTiming } from "./TimingEntry";
import UCUM from "../dictionaries/UCUM";
import ArrayEntry from "./ArrayEntry";
import BooleanEntry from "./BooleanEntry";
import StringEntry from "./StringEntry";
import IntegerEntry from "./IntegerEntry";

//expected input of Ratio datatype (object with {numerator:Quantity, denominator:SimpleQuantity}) SimpleQuantity = Quantity without comparator
//expected output if matching data structure with matching datatypes is "[ {numerator} / {denominator} ]"
//expected output if matching data structure but missing numerator is "[ [No Numerator] / {denominator} ]"
//expected output if matching data structure but missing denominator is "[ {numerator} / [No Denominator] ]"
//expected output if matching data structure but not matching datatypes is "[ERROR: Ratio data not interpretable.]"
//expected output if string datatype is "{ratio} [ERROR: Ratio data not in expected Ratio datatype.]"
//expected output if missing (undefined/null/'') is ''
//expected output if NaN, empty {}, empty [], or [a,b] is "[ERROR: Ratio data not interpretable.]"
//expected output if not otherwise specified is "[ERROR: Ratio data not interpretable.]"
const getRatioDisplay = (ratio) => {
    if (ratio === undefined || ratio === null || ratio === '') {
        return ""
    }
    if (typeof ratio === "object" && !Array.isArray(ratio) && (ratio.numerator || ratio.denominator)) {
        try {
            let numeratorDisplay = displayStringFromFHIRQuantity(ratio.numerator);
            let denominatorDisplay = displayStringFromFHIRQuantity(ratio.denominator);
            return `[ ${numeratorDisplay ? numeratorDisplay : "[No Numerator]"} / ${denominatorDisplay ? denominatorDisplay : "[No Denominator]"} ]`;
        } catch (e) {
            console.log("ERROR in getRatioDisplay");
            console.log(e);
        }
    }
    if (typeof ratio === "string") {
        return `${ratio} [ERROR: Ratio data not in expected Ratio datatype.]`
    }
    return "[ERROR: Ratio data not interpretable.]";
}

const getDoseAndRateDisplay = (doseAndRate) => {
    let doseAndRateDisplay = "";
    if (doseAndRate.doseRange) {
        doseAndRateDisplay = displayStringFromFHIRRange(doseAndRate.doseRange);
    }
    if (doseAndRate.doseQuantity) {
        doseAndRateDisplay = displayStringFromFHIRQuantity(doseAndRate.doseQuantity);
    }
    if (doseAndRate.type) {
        doseAndRateDisplay += " [" + displayStringFromFHIRCodeableConcept(doseAndRate.type) + "]";
    }
    if (doseAndRate.rateRatio || doseAndRate.rateRange || doseAndRate.rateQuantity) {
        if (doseAndRateDisplay) {
            doseAndRateDisplay += " ";
        }
        if (doseAndRate.rateRatio) {
            doseAndRateDisplay += getRatioDisplay(doseAndRate.rateRatio);
        }
        if (doseAndRate.rateRange) {
            doseAndRateDisplay += displayStringFromFHIRRange(doseAndRate.rateRange);
        }
        if (doseAndRate.rateQuantity) {
            doseAndRateDisplay += displayStringFromFHIRQuantity(doseAndRate.rateQuantity);
        }
    }
    return doseAndRateDisplay;
}

//expected input of Dosage datatype (object with {sequence:integer, text:string, 
//       additionalInstruction:[CodeableConcept], patientInstruction:string, timing:Timing, asNeeded:boolean, 
//       asNeededFor:[CodeableConcept], site:CodeableConcept,route:CodeableConcept, method:CodeableConcept,
//       doseAndRate: [{type:CodeableConcept, dose[x]:Range/SimpleQuantity, rate[x]:Ratio/Range/SimpleQuantity}], 
//       maxDosePerPeriod:[Ratio], maxDosePerAdministration:SimpleQuantity, maxDosePerLifetime:SimpleQuantity})
//expected output if matching data structure with matching datatypes is noted in the code script
//expected output if string datatype is "{dosage} [ERROR: Dosage data not in expected Dosage datatype.]"
//expected output if missing (undefined/null/''/{}) is ''
//expected output if NaN, empty [], or [a,b] is "[ERROR: Dosage data not interpretable.]"
//expected output if not otherwise specified is "[ERROR: Dosage data not interpretable.]"
const displayStringFromFHIRDosage = (dosage) => {
    if (dosage === undefined || dosage === null || dosage === '') {
        return ""
    }
    if (Array.isArray(dosage)) {
        return "[ERROR: Dosage data not interpretable.]";
    }
    if (typeof dosage === 'object') {
        let dosageDisplay = "";
        if (dosage.sequence) {
            dosageDisplay = "Dosage Sequence #" + dosage.sequence + ": ";
        }
        if (dosage.text) {
            dosageDisplay += dosage.text;
            return dosageDisplay;
        }
        if (dosage.method) {
            dosageDisplay += displayStringFromFHIRCodeableConcept(dosage.method) + " ";
        }
        if (dosage.doseAndRate?.length > 0) {
            dosageDisplay += dosage.doseAndRate.map((item, index) => getDoseAndRateDisplay(item)).join(', ');
        }
        if (dosage.route) {
            dosageDisplay += " via " + displayStringFromFHIRCodeableConcept(dosage.route);
        }
        if (dosage.site) {
            dosageDisplay += " applied to " + displayStringFromFHIRCodeableConcept(dosage.site);
        }
        if (dosage.asNeeded) {
            dosageDisplay += " as needed"
        }
        if (dosage.asNeededFor?.length > 0) {
            dosageDisplay += " as needed for " + dosage.asNeededFor.map((item, index) => displayStringFromFHIRCodeableConcept(item)).join(' -OR- ');
        }
        if (dosage.timing) {
            dosageDisplay += " " + displayStringFromFHIRTiming(dosage.timing);
        }
        if (dosage.maxDosePerPeriod?.length > 0) {
            dosageDisplay += "; MAXIMUM LIMIT " + dosage.maxDosePerPeriod.map((item, index) => getRatioDisplay(item)).join(', ');
        }
        if (dosage.maxDosePerAdministration) {
            dosageDisplay += "; MAXIMUM LIMIT PER DOSE " + displayStringFromFHIRQuantity(dosage.maxDosePerAdministration);
        }
        if (dosage.maxDosePerLifetime) {
            dosageDisplay += "; MAXIMUM CUMULATIVE LIMIT " + displayStringFromFHIRQuantity(dosage.maxDosePerLifetime);
        }
        if (dosage.additionalInstruction?.length > 0) {
            dosageDisplay += "; " + dosage.additionalInstruction.map((item, index) => displayStringFromFHIRCodeableConcept(item)).join('; ');
        }
        if (dosage.patientInstruction) {
            dosageDisplay += "; " + dosage.patientInstruction;
        }
        if (dosageDisplay && typeof dosageDisplay === 'string') {
            return dosageDisplay;
        }
        if (dosageDisplay === '') {
            return ""
        }
    }
    if (typeof dosage === "string") {
        return `${dosage} [ERROR: Dosage data not in expected Dosage datatype.]`;
    }
    return "[ERROR: Dosage data not interpretable.]";
}

const dosageDotAdditionalInstructionSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotAsNeededForSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotSiteSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotRouteSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const dosageDotMethodSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const DosageEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, debug, startCollapsed }) => {
  let startingDosage = {
    'sequence': "", 'text': "", 'additionalInstruction': [], 'patientInstruction': "", 'timing': "",
    'asNeeded': null, 'asNeededFor': [], 'site': "", 'route': "", 'method': "", 'doseAndRate': [],
    'maxDosePerPeriod': [], 'maxDosePerAdministration': "", 'maxDosePerLifetime': ""
  }
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingDosage.extension = startingValue.extension; }
    if (startingValue.sequence) { startingDosage.sequence = startingValue.sequence; }
    if (startingValue.text) { startingDosage.text = startingValue.text; }
    if (startingValue.additionalInstruction) { startingDosage.additionalInstruction = startingValue.additionalInstruction; }
    if (startingValue.patientInstruction) { startingDosage.patientInstruction = startingValue.patientInstruction; }
    if (startingValue.timing) { startingDosage.timing = startingValue.timing; }
    if (typeof startingValue.asNeeded === "boolean") { startingDosage.asNeeded = startingValue.asNeeded; }
    if (startingValue.asNeededFor) { startingDosage.asNeededFor = startingValue.asNeededFor; }
    if (startingValue.site) { startingDosage.site = startingValue.site; }
    if (startingValue.route) { startingDosage.route = startingValue.route; }
    if (startingValue.method) { startingDosage.method = startingValue.method; }
    if (startingValue.doseAndRate) { startingDosage.doseAndRate = startingValue.doseAndRate; }
    if (startingValue.maxDosePerPeriod) { startingDosage.maxDosePerPeriod = startingValue.maxDosePerPeriod; }
    if (startingValue.maxDosePerAdministration) { startingDosage.maxDosePerAdministration = startingValue.maxDosePerAdministration; }
    if (startingValue.maxDosePerLifetime) { startingDosage.maxDosePerLifetime = startingValue.maxDosePerLifetime; }
  }

  const [dosageState, setDosageState] = useState(JSON.parse(JSON.stringify(startingDosage || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(dosageState).length) {
      let newDosage = {};
      if (dosageState.extension) { newDosage.extension = dosageState.extension; }
      if (dosageState.sequence) { newDosage.sequence = dosageState.sequence; }
      if (dosageState.text) { newDosage.text = dosageState.text; }
      if (Array.isArray(dosageState.additionalInstruction) &&
        dosageState.additionalInstruction.length) {
        newDosage.additionalInstruction = dosageState.additionalInstruction;
      }
      if (dosageState.patientInstruction) { newDosage.patientInstruction = dosageState.patientInstruction; }
      if (dosageState.timing && Object.keys(dosageState.timing).length) {
        newDosage.timing = dosageState.timing;
      }
      if (typeof dosageState.asNeeded === "boolean") {
        newDosage.asNeeded = dosageState.asNeeded;
      }
      if (Array.isArray(dosageState.asNeededFor) && dosageState.asNeededFor.length) {
        newDosage.asNeededFor = dosageState.asNeededFor;
      }
      if (dosageState.site && Object.keys(dosageState.site).length) {
        newDosage.site = dosageState.site;
      }
      if (dosageState.route && Object.keys(dosageState.route).length) {
        newDosage.route = dosageState.route;
      }
      if (dosageState.method && Object.keys(dosageState.method).length) {
        newDosage.method = dosageState.method;
      }
      if (Array.isArray(dosageState.doseAndRate) &&
        dosageState.doseAndRate.length) {
        newDosage.doseAndRate = dosageState.doseAndRate;
      }
      if (Array.isArray(dosageState.maxDosePerPeriod) &&
        dosageState.maxDosePerPeriod.length) {
        newDosage.maxDosePerPeriod = dosageState.maxDosePerPeriod;
      }
      if (dosageState.maxDosePerAdministration && Object.keys(dosageState.maxDosePerAdministration).length) {
        newDosage.maxDosePerAdministration = dosageState.maxDosePerAdministration;
      }
      if (dosageState.maxDosePerLifetime && Object.keys(dosageState.maxDosePerLifetime).length) {
        newDosage.maxDosePerLifetime = dosageState.maxDosePerLifetime;
      }
      if (Object.keys(newDosage).length === 0) {
        newDosage = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDosage }; });
    }
  }), [dosageState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRDosage(startingDosage)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <IntegerEntry elementName='sequence'
          fieldLabel='Dosage Sequence' startingValue={dosageState.sequence}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <StringEntry elementName='text'
          fieldLabel='Dosage Instruction' startingValue={dosageState.text}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <ArrayEntry elementName='additionalInstruction'
          datatype='CodeableConcept' fieldLabel='Additional Instruction' startingValue={dosageState.additionalInstruction}
          systemChoices={dosageDotAdditionalInstructionSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <StringEntry elementName='patientInstruction'
          fieldLabel='Patient Instruction' startingValue={dosageState.patientInstruction}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <TimingEntry elementName='timing'
          fieldLabel='Timing' startingValue={dosageState.timing}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <BooleanEntry elementName='asNeeded'
          fieldLabel='As Needed' startingValue={dosageState.asNeeded}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <ArrayEntry elementName='asNeededFor'
          datatype='CodeableConcept' fieldLabel='As Needed For' startingValue={dosageState.asNeededFor}
          systemChoices={dosageDotAsNeededForSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <CodeableConceptEntry elementName='site'
          fieldLabel='Body Site' startingValue={dosageState.site}
          systemChoices={dosageDotSiteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <CodeableConceptEntry elementName='route'
          fieldLabel='Route' startingValue={dosageState.route}
          systemChoices={dosageDotRouteSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <CodeableConceptEntry elementName='method'
          fieldLabel='Method' startingValue={dosageState.method}
          systemChoices={dosageDotMethodSystemChoices} systemChoicesOpen
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <ArrayEntry elementName='doseAndRate'
          datatype='DoseAndRate' fieldLabel='Dose and Rate' startingValue={dosageState.doseAndRate}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <ArrayEntry elementName='maxDosePerPeriod'
          datatype='Ratio' fieldLabel='Maximum Dose per Period' startingValue={dosageState.maxDosePerPeriod}
          setResourceState={setDosageState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <QuantityEntry elementName='maxDosePerAdministration'
          fieldLabel='Maximum Dose per Administration' startingValue={dosageState.maxDosePerAdministration}
          setResourceState={setDosageState} dataEntryStyle={'SimpleQuantity'} inTableCell={inTableCell} debug={debug} />
        <QuantityEntry elementName='maxDosePerLifetime'
          fieldLabel='Maximum Dose per Lifetime' startingValue={dosageState.maxDosePerLifetime}
          setResourceState={setDosageState} dataEntryStyle={'SimpleQuantity'} inTableCell={inTableCell} debug={debug} />
      </div>
    </>
  }
});

const doseAndRateDotTypeValueSet = [
  { system: "https://terminology.hl7.org/5.0.0/CodeSystem-dose-rate-type.html", code: "calculated", display: "Calculated" },
  { system: "https://terminology.hl7.org/5.0.0/CodeSystem-dose-rate-type.html", code: "ordered", display: "Ordered" }
]
const doseAndRateDotDoseAllowedDatatypes = ['Range', 'SimpleQuantity'];
const doseAndRateDotRateAllowedDatatypes = ['Ratio', 'Range', 'SimpleQuantity'];
const DoseAndRateEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, debug, startCollapsed }) => {
  let startingDoseAndRate = {
    'type': "", 'doseRange': "", 'doseQuantity': "",
    'rateRatio': "", 'rateRange': "", 'rateQuantity': ""
  };
  let startingDoseDatatype = 'none';
  let startingRateDatatype = 'none';
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingDoseAndRate.extension = startingValue.extension; }
    if (startingValue.type) { startingDoseAndRate.type = startingValue.type; }
    if (startingValue.doseRange) {
      startingDoseAndRate.doseRange = startingValue.doseRange;
      startingDoseDatatype = 'Range';
    }
    if (startingValue.doseQuantity) {
      startingDoseAndRate.doseQuantity = startingValue.doseQuantity;
      startingDoseDatatype = 'SimpleQuantity';
    }
    if (startingValue.rateRatio) {
      startingDoseAndRate.rateRatio = startingValue.rateRatio;
      startingRateDatatype = 'Ratio';
    }
    if (startingValue.rateRange) {
      startingDoseAndRate.rateRange = startingValue.rateRange;
      startingRateDatatype = 'Range';
    }
    if (startingValue.rateQuantity) {
      startingDoseAndRate.rateQuantity = startingValue.rateQuantity;
      startingRateDatatype = 'SimpleQuantity';
    }
  }

  const [doseAndRateState, setDoseAndRateState] = useState(JSON.parse(JSON.stringify(startingDoseAndRate || {})));

  const [doseDatatypeState, setDoseDatatypeState] = useState(startingDoseDatatype);
  const [rateDatatypeState, setRateDatatypeState] = useState(startingRateDatatype);
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(doseAndRateState).length) {
      let newDoseAndRate = {};
      if (doseAndRateState.extension) { newDoseAndRate.extension = doseAndRateState.extension; }
      if (doseAndRateState.type && Object.keys(doseAndRateState.type).length) {
        newDoseAndRate.type = doseAndRateState.type;
      }
      if (doseDatatypeState === 'Range' && doseAndRateState.doseRange && Object.keys(doseAndRateState.doseRange).length) {
        newDoseAndRate.doseRange = doseAndRateState.doseRange;
      }
      if (doseDatatypeState === 'SimpleQuantity' && doseAndRateState.doseQuantity && Object.keys(doseAndRateState.doseQuantity).length) {
        newDoseAndRate.doseQuantity = doseAndRateState.doseQuantity;
      }
      if (rateDatatypeState === 'Ratio' && doseAndRateState.rateRatio && Object.keys(doseAndRateState.rateRatio).length) {
        newDoseAndRate.rateRatio = doseAndRateState.rateRatio;
      }
      if (rateDatatypeState === 'Range' && doseAndRateState.rateRange && Object.keys(doseAndRateState.rateRange).length) {
        newDoseAndRate.rateRange = doseAndRateState.rateRange;
      }
      if (rateDatatypeState === 'SimpleQuantity' && doseAndRateState.rateQuantity && Object.keys(doseAndRateState.rateQuantity).length) {
        newDoseAndRate.rateQuantity = doseAndRateState.rateQuantity;
      }
      if (Object.keys(newDoseAndRate).length === 0) {
        newDoseAndRate = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newDoseAndRate }; });
    }
  }), [doseAndRateState, doseDatatypeState, rateDatatypeState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && getDoseAndRateDisplay(startingDoseAndRate)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <CodeableConceptEntry elementName='type' fieldLabel='Type'
          startingValue={doseAndRateState.type} setResourceState={setDoseAndRateState}
          valueSet={doseAndRateDotTypeValueSet}
          dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} />
        <p style={{ marginBottom: "0px" }}><b>Dose:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='dose[x]' allowedDatatypes={doseAndRateDotDoseAllowedDatatypes}
            inTableCell={inTableCell} datatypeState={doseDatatypeState} setDatatypeState={setDoseDatatypeState} />
          {doseDatatypeState === 'Range' &&
            <RangeEntry elementName='doseRange' fieldLabel={'Range per Dose'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.doseRange} setResourceState={setDoseAndRateState} />}
          {doseDatatypeState === 'SimpleQuantity' &&
            <QuantityEntry elementName='doseQuantity' fieldLabel={'Quantity per Dose'}
              dataEntryStyle={"SimpleQuantity"} inTableCell={inTableCell}
              startingValue={doseAndRateState.doseQuantity} setResourceState={setDoseAndRateState} />}
        </div>
        <p style={{ marginBottom: "0px" }}><b>Rate:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='rate[x]' allowedDatatypes={doseAndRateDotRateAllowedDatatypes}
            datatypeState={rateDatatypeState} setDatatypeState={setRateDatatypeState} />
          {rateDatatypeState === 'Ratio' &&
            <RatioEntry elementName='rateRatio' fieldLabel={'Ratio for Dose per Time'}
              startingValue={doseAndRateState.rateRatio} setResourceState={setDoseAndRateState} />}
          {rateDatatypeState === 'Range' &&
            <RangeEntry elementName='rateRange' fieldLabel={'Range for Dose per Time'}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={doseAndRateState.rateRange} setResourceState={setDoseAndRateState} />}
          {rateDatatypeState === 'SimpleQuantity' &&
            <QuantityEntry elementName='rateQuantity' fieldLabel={'Quantity for Dose per Time'}
              dataEntryStyle={"SimpleQuantity"} inTableCell={inTableCell}
              startingValue={doseAndRateState.rateQuantity} setResourceState={setDoseAndRateState} />}
        </div>
      </div>
    </>
  }
})


const RatioEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle,
  numeratorValueSet, denominatorValueSet, startCollapsed, inTableCell }) => {
  let startingRatio = { numerator: "", denominator: "" };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingRatio.extension = startingValue.extension; }
    if (startingValue.numerator) { startingRatio.numerator = startingValue.numerator; }
    if (startingValue.denominator) { startingRatio.denominator = startingValue.denominator; }
  }
  if (numeratorValueSet === "Age") {
    numeratorValueSet = UCUM.age;
  } else if (numeratorValueSet === "Duration") {
    numeratorValueSet = UCUM.duration;
  } else if (numeratorValueSet === "UCUM") {
    numeratorValueSet = UCUM.common;
  }
  if (denominatorValueSet === "Age") {
    denominatorValueSet = UCUM.age;
  } else if (denominatorValueSet === "Duration") {
    denominatorValueSet = UCUM.duration;
  } else if (denominatorValueSet === "UCUM") {
    denominatorValueSet = UCUM.common;
  }

  const [ratioState, setRatioState] = useState(JSON.parse(JSON.stringify(startingRatio || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(ratioState).length) {
      let newRatio = {};
      if (ratioState.extension) { newRatio.extension = ratioState.extension; }
      if (ratioState.numerator && Object.keys(ratioState.numerator).length) {
        newRatio.numerator = ratioState.numerator;
      }
      if (ratioState.denominator && Object.keys(ratioState.denominator).length) {
        newRatio.denominator = ratioState.denominator;
      }
      if (Object.keys(newRatio).length === 0) {
        newRatio = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newRatio }; });
    }
  }), [ratioState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && getRatioDisplay(startingRatio)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <QuantityEntry elementName='numerator'
          fieldLabel='Numerator' startingValue={ratioState.numerator} inTableCell={inTableCell}
          numeratorValueSet={numeratorValueSet} setResourceState={setRatioState} />
        <QuantityEntry elementName='denominator'
          fieldLabel='Denominator' startingValue={ratioState.denominator} dataEntryStyle='SimpleQuantity'
          inTableCell={inTableCell}
          denominatorValueSet={denominatorValueSet} setResourceState={setRatioState} />
      </div>
    </>
  }
});

export default DosageEntry;
export { displayStringFromFHIRDosage, DoseAndRateEntry, getRatioDisplay, RatioEntry };